import type { JobDocumentUploadFailure } from './uploadJobDocuments';

/** Most entries to name before collapsing the rest into a count. */
const MAX_LISTED = 3;

/**
 * Human-readable summary of the attachments that could not be uploaded, for the
 * body of a warning toast. Returns null when nothing failed so callers can skip
 * the toast entirely (CWL-115 / TEST 2, 6, 8). The result is deliberately a
 * single line, capped at MAX_LISTED entries — ToastDescription
 * (shared/ui/toast.tsx:105-117) renders with normal white-space, so a
 * newline-joined or uncapped list would run together or overflow the toast.
 */
export function describeUploadFailures(failures: JobDocumentUploadFailure[]): string | null {
  if (failures.length === 0) return null;
  const listed = failures
    .slice(0, MAX_LISTED)
    .map((failure) => `${failure.fileName} — ${failure.reason}`);
  const remaining = failures.length - listed.length;
  if (remaining > 0) listed.push(`and ${remaining} more`);
  return listed.join('; ');
}

/** One job's failed uploads, for a batched caller that warns once. */
export interface JobUploadFailureGroup {
  /** Human-facing job identifier — the job name shown in the list. */
  jobLabel: string;
  failures: JobDocumentUploadFailure[];
}

/**
 * Same contract as describeUploadFailures, but for a batch: each entry is
 * prefixed with its job so a bulk reject's single warning says which job lost
 * which file (CWL-115 R001-F015). Capped on the combined total, not per job.
 */
export function describeJobUploadFailures(groups: JobUploadFailureGroup[]): string | null {
  const entries = groups.flatMap(({ jobLabel, failures }) =>
    failures.map((failure) => `${jobLabel}: ${failure.fileName} — ${failure.reason}`),
  );
  if (entries.length === 0) return null;
  const listed = entries.slice(0, MAX_LISTED);
  const remaining = entries.length - listed.length;
  if (remaining > 0) listed.push(`and ${remaining} more`);
  return listed.join('; ');
}
